import { produce } from 'immer';
import { message } from 'antd';
import { transformTextToEditorState } from '../utils/dataSync';
import { getBoxById } from './lookups';
import { applyDiff } from '../collab/diffEngine';
import { deserializeEntity } from '../collab/entitySerializer';
import { withRemoteGuard } from '../collab/remoteGuard';

// État de la collaboration temps réel (présence + verrous par box).
// Alimenté par les messages reçus sur EditorCollabChannel (cf. src/collab/useEditorCollab.js).
export const collabActions = (set, get) => ({
  // true uniquement quand l'abonnement au channel est confirmé par le serveur.
  // Si le back n'expose pas (encore) le channel, reste false et la collab est inactive.
  collabConnected: false,
  presence: [], // [{ id, name, color }]
  boxLocks: {}, // { [boxId]: { id, name, color } } — détenteur du verrou (soi inclus)
  // true tant qu'au moins une requête de persistance granulaire (autosave) est
  // en vol — piloté par un compteur de requêtes dans src/collab/persistence.js.
  isAutosaving: false,
  // false si tout est enregistré ; 'retrying' tant que des modifications
  // attendent un nouvel essai ou la reconnexion ; 'rejected' après un échec
  // non rejouable, en attente d'un enregistrement manuel. Piloté par
  // src/collab/persistence.js, remis à false au changement de document.
  autosaveFailed: false,
  // Incrémenté quand un envoi réussi laisse tout enregistré après un échec :
  // seul signal d'une vraie reprise (autosaveFailed repasse aussi à false
  // quand la file est abandonnée — changement de document, restauration).
  autosaveRecoveries: 0,
  // true pendant restoreContentVersion (appel API + reload). Pilote le claim du
  // verrou partagé reserved:restore (useEditorCollab) et l'overlay bloquant.
  isRestoringVersion: false,
  // true si le serveur a révoqué cette session (même user connecté ailleurs,
  // autre onglet/appareil) : cet onglet passe en lecture seule définitive
  // jusqu'à rechargement. Cf. message `session_revoked` (useEditorCollab.js).
  sessionRevoked: false,

  setSessionRevoked: (revoked) => {
    set(
      produce((draft) => {
        draft.sessionRevoked = revoked;
      }),
      false,
      `Collab session revoked: ${revoked}`
    );
  },

  setIsRestoringVersion: (restoring) => {
    set(
      produce((draft) => {
        draft.isRestoringVersion = restoring;
      }),
      false,
      `Collab restoring version: ${restoring}`
    );
  },

  setAutosaveFailed: (failed) => {
    set(
      produce((draft) => {
        draft.autosaveFailed = failed;
      }),
      false,
      `Collab autosave failed: ${failed}`
    );
  },

  markAutosaveRecovered: () => {
    set(
      produce((draft) => {
        draft.autosaveRecoveries += 1;
      }),
      false,
      'Collab autosave recovered'
    );
  },

  setAutosaving: (saving) => {
    set(
      produce((draft) => {
        draft.isAutosaving = saving;
      }),
      false,
      `Collab autosaving: ${saving}`
    );
  },

  // Réponse de sauvegarde : l'opportunité du document a pu être cachée (ou
  // ré-affichée) depuis un autre onglet pendant que l'éditeur restait ouvert.
  setDocumentHidden: (hidden) => {
    set(
      produce((draft) => {
        if (draft.documentIdentity) draft.documentIdentity.hidden = hidden;
      }),
      false,
      `Document hidden: ${hidden}`
    );
  },

  setCollabConnected: (connected) => {
    set(
      produce((draft) => {
        draft.collabConnected = connected;
        if (!connected) {
          draft.presence = [];
          draft.boxLocks = {};
        }
      }),
      false,
      `Collab connected: ${connected}`
    );
  },

  setPresence: (users) => {
    set(
      produce((draft) => {
        draft.presence = users ?? [];
      }),
      false,
      `Collab presence`
    );
  },

  setBoxLocked: (boxId, user) => {
    set(
      produce((draft) => {
        draft.boxLocks[boxId] = user;
      }),
      false,
      `Collab box locked ${boxId}`
    );
  },

  setBoxUnlocked: (boxId) => {
    set(
      produce((draft) => {
        delete draft.boxLocks[boxId];
      }),
      false,
      `Collab box unlocked ${boxId}`
    );
  },

  // État complet des verrous transmis par le serveur au subscribe (client
  // rejoignant un document où des verrous existent déjà)
  setAllBoxLocks: (locks) => {
    set(
      produce((draft) => {
        draft.boxLocks = locks ?? {};
      }),
      false,
      'Collab locks state'
    );
  },

  // Contenu reçu d'un co-éditeur (box_patched) : injecté dans le store via
  // updateBox (même chemin que saveBoxImmediate — gère doc et mask boxes).
  // Les éditeurs non sélectionnés se resynchronisent déjà depuis le store.
  applyRemoteBoxPatch: (boxId, raw) => {
    const { boxes, masks, updateBox } = get();
    // Box supprimée entre-temps (course) : rien à faire
    if (!getBoxById(boxes, masks, boxId)) return;
    withRemoteGuard(() => {
      updateBox(
        boxId,
        (box) => {
          box.content.editorState = transformTextToEditorState(raw);
        },
        { actionNameSuffix: 'from_collab' }
      );
    });
  },

  // Diff structurel reçu d'un co-éditeur : upserts/deletes/ordre appliqués au
  // store derrière l'echo guard. L'appelant (useEditorCollab) flush le diff
  // local en attente avant, et réaligne la baseline après.
  applyRemoteStructurePatch: (wireDiff) => {
    const diff = deserializeEntity(wireDiff);
    if ((diff.deletes?.boxes ?? []).includes(get().selectedBoxId)) {
      message.warning('Le bloc que vous éditiez a été supprimé par un co-éditeur');
      get().handleUnSelectBox('collab remote delete');
    }
    // Single-writer strict : un upsert distant ne remplace jamais une entité
    // que j'édite (box sélectionnée / variable ouverte au formulaire) — les
    // cascades type remplacement de variables chez un co-éditeur écraseraient
    // mes frappes locales. Ma version repartira via mon propre diff.
    const editedIds = [get().selectedBoxId, get().boxVariableSelectedId].filter(
      Boolean
    );
    if (editedIds.length && diff.upserts?.boxes) {
      diff.upserts.boxes = diff.upserts.boxes.filter(
        (b) => !editedIds.includes(b.id)
      );
    }
    withRemoteGuard(() => {
      set(applyDiff(get(), diff), false, 'Collab structure patched');
    });
  },
});
